/**
 * File Upload Component
 *
 * A drag-and-drop file upload component for expenditure request attachments.
 * Features:
 * - Drag-and-drop upload zone
 * - Click to select files
 * - Upload progress indicator
 * - Uploaded files list with remove option
 * - File size validation (max 5MB)
 * - File type icons
 * - Error handling
 * - Multiple file support
 *
 * Styled with Tailwind CSS using glass morphism for dark theme.
 */

import { renderIcon, type IconName } from '../utils/icons';
import { apiClient } from '../services/api-client';
import type { ValidationState } from '../types/form';

/**
 * Maximum file size in bytes (5MB as per CLAUDE.md)
 */
export const MAX_FILE_SIZE = 5 * 1024 * 1024;

/**
 * Maximum number of files per upload
 */
export const MAX_FILES_PER_UPLOAD = 10;

/**
 * Allowed MIME types for file uploads
 */
export const ALLOWED_MIME_TYPES = [
  // Documents
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  // Images
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  // Text
  'text/plain',
  'text/csv',
] as const;

/**
 * File type to icon mapping
 */
const FILE_TYPE_ICONS: Record<string, IconName> = {
  'application/pdf': 'fileText',
  'application/msword': 'fileText',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'fileText',
  'application/vnd.ms-excel': 'fileText',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'fileText',
  'image/jpeg': 'image',
  'image/png': 'image',
  'image/gif': 'image',
  'image/webp': 'image',
  'text/plain': 'fileText',
  'text/csv': 'fileText',
};

/**
 * Uploaded file interface
 */
export interface UploadedFile {
  /** Unique identifier */
  id: string;
  /** Original filename */
  name: string;
  /** File size in bytes */
  size: number;
  /** MIME type */
  type: string;
  /** Upload progress (0-100) */
  progress: number;
  /** Upload status */
  status: 'pending' | 'uploading' | 'completed' | 'error';
  /** Error message if any */
  error?: string;
  /** Server-side attachment ID after upload */
  attachmentId?: string;
}

/**
 * File upload options
 */
export interface FileUploadOptions {
  /** Request ID for attaching files */
  requestId?: string;
  /** Container element */
  container?: HTMLElement;
  /** Whether uploads are disabled */
  disabled?: boolean;
  /** Maximum number of files */
  maxFiles?: number;
  /** Maximum file size in bytes */
  maxFileSize?: number;
  /** Allowed MIME types */
  allowedTypes?: readonly string[];
  /** Whether to auto-upload on selection */
  autoUpload?: boolean;
  /** Initial files (already uploaded) */
  initialFiles?: UploadedFile[];
  /** Callback when files change */
  onChange?: (files: UploadedFile[]) => void;
  /** Callback when upload completes */
  onUploadComplete?: (file: UploadedFile) => void;
  /** Callback when upload fails */
  onUploadError?: (file: UploadedFile, error: string) => void;
  /** Callback when file is removed */
  onRemove?: (file: UploadedFile) => void;
  /** Custom upload function */
  uploadFn?: (file: File, onProgress: (progress: number) => void) => Promise<{ attachmentId: string }>;
  /** Additional CSS classes */
  className?: string;
  /** Validation state */
  validationState?: ValidationState;
  /** Helper text */
  helperText?: string;
}

/**
 * Generate a unique ID
 */
function generateId(): string {
  return `file-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`;
}

/**
 * Get icon for file type
 */
export function getFileIcon(mimeType: string): IconName {
  return FILE_TYPE_ICONS[mimeType] || 'file';
}

/**
 * Validate a file before upload
 */
export function validateFile(
  file: File,
  options: { maxSize?: number; allowedTypes?: readonly string[] } = {}
): { valid: boolean; error?: string } {
  const maxSize = options.maxSize ?? MAX_FILE_SIZE;
  const allowedTypes = options.allowedTypes ?? ALLOWED_MIME_TYPES;

  // Check file size
  if (file.size > maxSize) {
    return {
      valid: false,
      error: `File size exceeds maximum of ${formatFileSize(maxSize)}`,
    };
  }

  // Check MIME type
  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `File type "${file.type}" is not allowed`,
    };
  }

  return { valid: true };
}

/**
 * CSS classes for the component
 */
const DROPZONE_BASE_CLASSES = [
  'relative',
  'flex',
  'flex-col',
  'items-center',
  'justify-center',
  'w-full',
  'min-h-[160px]',
  'p-6',
  'rounded-xl',
  'border-2',
  'border-dashed',
  'bg-neutral-950/40',
  'backdrop-blur-sm',
  'transition-all',
  'duration-200',
  'cursor-pointer',
].join(' ');

const DROPZONE_STATE_CLASSES: Record<string, string> = {
  default: 'border-neutral-700 hover:border-neutral-600 hover:bg-neutral-950/60',
  dragging: 'border-primary bg-primary/10 scale-[1.01]',
  disabled: 'border-neutral-800 bg-neutral-950/20 cursor-not-allowed opacity-50',
  error: 'border-destructive hover:border-destructive',
};

/**
 * File Upload Component Class
 */
export class FileUpload {
  private container: HTMLElement;
  private options: FileUploadOptions;
  private element: HTMLElement | null = null;
  private dropzone: HTMLElement | null = null;
  private fileInput: HTMLInputElement | null = null;
  private fileListContainer: HTMLElement | null = null;
  private helperTextElement: HTMLElement | null = null;

  private files: UploadedFile[] = [];
  private isDisabled = false;

  constructor(options: FileUploadOptions = {}) {
    this.options = {
      maxFiles: MAX_FILES_PER_UPLOAD,
      maxFileSize: MAX_FILE_SIZE,
      allowedTypes: ALLOWED_MIME_TYPES,
      autoUpload: true,
      initialFiles: [],
      ...options,
    };

    this.container = options.container || document.createElement('div');
    this.isDisabled = options.disabled || false;
    this.files = [...(options.initialFiles || [])];

    this.render();
    this.attachEventListeners();
  }

  /**
   * Render the component
   */
  private render(): void {
    this.element = document.createElement('div');
    this.element.className = `file-upload-component ${this.options.className || ''}`.trim();

    // Create dropzone
    this.dropzone = this.createDropzone();
    this.element.appendChild(this.dropzone);

    // Create hidden file input
    this.fileInput = this.createFileInput();
    this.element.appendChild(this.fileInput);

    // Create file list container
    this.fileListContainer = document.createElement('div');
    this.fileListContainer.className = 'file-list mt-4 space-y-2';
    this.element.appendChild(this.fileListContainer);

    // Create helper text element
    if (this.options.helperText || this.options.validationState === 'error') {
      this.helperTextElement = this.createHelperText();
      this.element.appendChild(this.helperTextElement);
    }

    // Render initial files
    this.renderFileList();

    // Append to container
    this.container.appendChild(this.element);
  }

  /**
   * Create the dropzone element
   */
  private createDropzone(): HTMLElement {
    const dropzone = document.createElement('div');
    const stateClass = this.isDisabled ? DROPZONE_STATE_CLASSES.disabled : DROPZONE_STATE_CLASSES.default;
    dropzone.className = `${DROPZONE_BASE_CLASSES} ${stateClass}`;

    // Icon
    const iconWrapper = document.createElement('div');
    iconWrapper.className = 'w-12 h-12 rounded-xl bg-neutral-800/50 flex items-center justify-center mb-4';
    iconWrapper.appendChild(renderIcon('upload', { size: 'lg', className: 'text-muted-foreground' }));
    dropzone.appendChild(iconWrapper);

    // Text
    const textWrapper = document.createElement('div');
    textWrapper.className = 'text-center';

    const mainText = document.createElement('p');
    mainText.className = 'text-sm font-medium text-foreground mb-1';
    mainText.innerHTML = '<span class="text-primary">Click to upload</span> or drag and drop';

    const subText = document.createElement('p');
    subText.className = 'text-xs text-muted-foreground';
    const allowedExtensions = this.getAllowedExtensions();
    subText.textContent = `${allowedExtensions} (max ${formatFileSize(this.options.maxFileSize!)})`;

    textWrapper.appendChild(mainText);
    textWrapper.appendChild(subText);
    dropzone.appendChild(textWrapper);

    return dropzone;
  }

  /**
   * Get allowed file extensions for display
   */
  private getAllowedExtensions(): string {
    const extensions = new Set<string>();
    const types = this.options.allowedTypes || ALLOWED_MIME_TYPES;

    types.forEach((type) => {
      if (type.includes('pdf')) extensions.add('PDF');
      if (type.includes('word') || type.includes('msword')) extensions.add('DOC');
      if (type.includes('excel') || type.includes('spreadsheet')) extensions.add('XLS');
      if (type.includes('image')) {
        if (type.includes('jpeg')) extensions.add('JPG');
        if (type.includes('png')) extensions.add('PNG');
        if (type.includes('gif')) extensions.add('GIF');
        if (type.includes('webp')) extensions.add('WEBP');
      }
      if (type.includes('text/plain')) extensions.add('TXT');
      if (type.includes('csv')) extensions.add('CSV');
    });

    return Array.from(extensions).join(', ');
  }

  /**
   * Create the hidden file input
   */
  private createFileInput(): HTMLInputElement {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.className = 'sr-only';
    input.id = generateId();
    input.accept = (this.options.allowedTypes || ALLOWED_MIME_TYPES).join(',');
    input.disabled = this.isDisabled;

    return input;
  }

  /**
   * Create helper text element
   */
  private createHelperText(): HTMLElement {
    const helper = document.createElement('p');
    const isError = this.options.validationState === 'error';
    helper.className = `text-xs mt-2 ${isError ? 'text-destructive' : 'text-muted-foreground'}`;
    helper.textContent = this.options.helperText || '';
    return helper;
  }

  /**
   * Attach event listeners
   */
  private attachEventListeners(): void {
    if (!this.dropzone || !this.fileInput) return;

    // Click to open file dialog
    this.dropzone.addEventListener('click', () => {
      if (!this.isDisabled) {
        this.fileInput?.click();
      }
    });

    // File input change
    this.fileInput.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        this.handleFiles(Array.from(target.files));
        target.value = ''; // Reset input
      }
    });

    // Drag and drop events
    this.dropzone.addEventListener('dragenter', (e) => this.handleDragEnter(e));
    this.dropzone.addEventListener('dragover', (e) => this.handleDragOver(e));
    this.dropzone.addEventListener('dragleave', (e) => this.handleDragLeave(e));
    this.dropzone.addEventListener('drop', (e) => this.handleDrop(e));

    // Prevent default drag behavior on document
    document.addEventListener('dragover', (e) => e.preventDefault());
    document.addEventListener('drop', (e) => e.preventDefault());
  }

  /**
   * Handle drag enter
   */
  private handleDragEnter(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();
    if (this.isDisabled) return;

    // Dragging state handled by CSS classes
    this.updateDropzoneState('dragging');
  }

  /**
   * Handle drag over
   */
  private handleDragOver(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();
    if (this.isDisabled) return;

    // Dragging state handled by CSS classes
    this.updateDropzoneState('dragging');
  }

  /**
   * Handle drag leave
   */
  private handleDragLeave(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();

    // Only handle if leaving the dropzone
    const relatedTarget = e.relatedTarget as Node | null;
    if (this.dropzone && relatedTarget && this.dropzone.contains(relatedTarget)) {
      return;
    }

    // Dragging state handled by CSS classes
    this.updateDropzoneState(this.isDisabled ? 'disabled' : 'default');
  }

  /**
   * Handle drop
   */
  private handleDrop(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();

    // Dragging state handled by CSS classes
    this.updateDropzoneState(this.isDisabled ? 'disabled' : 'default');

    if (this.isDisabled) return;

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFiles(Array.from(files));
    }
  }

  /**
   * Update dropzone visual state
   */
  private updateDropzoneState(state: string): void {
    if (!this.dropzone) return;

    // Remove all state classes
    Object.values(DROPZONE_STATE_CLASSES).forEach((classes) => {
      classes.split(' ').forEach((cls) => {
        this.dropzone?.classList.remove(cls);
      });
    });

    // Add new state classes
    const stateClasses = DROPZONE_STATE_CLASSES[state] ?? DROPZONE_STATE_CLASSES.default;
    stateClasses?.split(' ').forEach((cls) => {
      this.dropzone?.classList.add(cls);
    });
  }

  /**
   * Handle selected files
   */
  private handleFiles(selectedFiles: File[]): void {
    // Check max files limit
    const currentCount = this.files.filter((f) => f.status !== 'error').length;
    const maxFiles = this.options.maxFiles || MAX_FILES_PER_UPLOAD;

    if (currentCount + selectedFiles.length > maxFiles) {
      this.showError(`Maximum ${maxFiles} files allowed`);
      return;
    }

    // Process each file
    for (const file of selectedFiles) {
      // Validate file - build options object without undefined values
      const validationOptions: { maxSize?: number; allowedTypes?: readonly string[] } = {};
      if (this.options.maxFileSize !== undefined) validationOptions.maxSize = this.options.maxFileSize;
      if (this.options.allowedTypes !== undefined) validationOptions.allowedTypes = this.options.allowedTypes;
      const validation = validateFile(file, validationOptions);

      const uploadedFile: UploadedFile = {
        id: generateId(),
        name: file.name,
        size: file.size,
        type: file.type,
        progress: 0,
        status: validation.valid ? 'pending' : 'error',
        ...(validation.error !== undefined && { error: validation.error }),
      };

      this.files.push(uploadedFile);

      // Auto-upload if enabled and valid
      if (validation.valid && this.options.autoUpload) {
        this.uploadFile(file, uploadedFile);
      }
    }

    this.renderFileList();
    this.notifyChange();
  }

  /**
   * Upload a file
   */
  private async uploadFile(file: File, uploadedFile: UploadedFile): Promise<void> {
    uploadedFile.status = 'uploading';
    this.renderFileList();

    try {
      let result: { attachmentId: string };

      if (this.options.uploadFn) {
        // Use custom upload function
        result = await this.options.uploadFn(file, (progress) => {
          uploadedFile.progress = progress;
          this.renderFileList();
        });
      } else if (this.options.requestId) {
        // Use default API upload
        result = await this.uploadToApi(file, uploadedFile);
      } else {
        // Simulate upload for preview mode
        result = await this.simulateUpload(uploadedFile);
      }

      uploadedFile.status = 'completed';
      uploadedFile.progress = 100;
      uploadedFile.attachmentId = result.attachmentId;

      this.options.onUploadComplete?.(uploadedFile);
    } catch (error) {
      uploadedFile.status = 'error';
      uploadedFile.error = error instanceof Error ? error.message : 'Upload failed';

      this.options.onUploadError?.(uploadedFile, uploadedFile.error);
    }

    this.renderFileList();
    this.notifyChange();
  }

  /**
   * Upload file to API
   */
  private async uploadToApi(file: File, uploadedFile: UploadedFile): Promise<{ attachmentId: string }> {
    const formData = new FormData();
    formData.append('files', file);

    // Use XMLHttpRequest for progress tracking
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          uploadedFile.progress = Math.round((e.loaded / e.total) * 100);
          this.renderFileList();
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            const attachmentId = response.data?.[0]?.id;
            if (attachmentId) {
              resolve({ attachmentId });
            } else {
              reject(new Error('Invalid response from server'));
            }
          } catch {
            reject(new Error('Failed to parse server response'));
          }
        } else {
          try {
            const errorResponse = JSON.parse(xhr.responseText);
            reject(new Error(errorResponse.message || 'Upload failed'));
          } catch {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload was cancelled'));
      });

      xhr.open('POST', `/api/requests/${this.options.requestId}/attachments`);

      // Get auth token from apiClient if available
      const token = localStorage.getItem('auth_token');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }

      xhr.send(formData);
    });
  }

  /**
   * Simulate upload for preview mode
   */
  private simulateUpload(uploadedFile: UploadedFile): Promise<{ attachmentId: string }> {
    return new Promise((resolve) => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += 10;
        uploadedFile.progress = progress;
        this.renderFileList();

        if (progress >= 100) {
          clearInterval(interval);
          resolve({ attachmentId: generateId() });
        }
      }, 100);
    });
  }

  /**
   * Render the file list
   */
  private renderFileList(): void {
    if (!this.fileListContainer) return;

    this.fileListContainer.innerHTML = '';

    if (this.files.length === 0) {
      return;
    }

    for (const file of this.files) {
      const fileItem = this.createFileItem(file);
      this.fileListContainer.appendChild(fileItem);
    }
  }

  /**
   * Create a file item element
   */
  private createFileItem(file: UploadedFile): HTMLElement {
    const item = document.createElement('div');
    item.className = 'file-item flex items-center gap-3 p-3 rounded-lg bg-neutral-900/60 border border-neutral-800';
    item.dataset.fileId = file.id;

    // File icon
    const iconWrapper = document.createElement('div');
    iconWrapper.className = 'w-10 h-10 rounded-lg bg-neutral-800/50 flex items-center justify-center shrink-0';

    const iconName = getFileIcon(file.type);
    const iconClass = file.status === 'error' ? 'text-destructive' : 'text-muted-foreground';
    iconWrapper.appendChild(renderIcon(iconName, { size: 'md', className: iconClass }));
    item.appendChild(iconWrapper);

    // File info
    const infoWrapper = document.createElement('div');
    infoWrapper.className = 'flex-1 min-w-0';

    const nameRow = document.createElement('div');
    nameRow.className = 'flex items-center gap-2';

    const fileName = document.createElement('p');
    fileName.className = 'text-sm font-medium text-foreground truncate';
    fileName.textContent = file.name;
    fileName.title = file.name;
    nameRow.appendChild(fileName);

    // Status badge
    if (file.status === 'completed') {
      const badge = document.createElement('span');
      badge.className = 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs bg-success/20 text-success';
      badge.appendChild(renderIcon('check', { size: 'xs' }));
      nameRow.appendChild(badge);
    } else if (file.status === 'error') {
      const badge = document.createElement('span');
      badge.className = 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs bg-destructive/20 text-destructive';
      badge.appendChild(renderIcon('alertCircle', { size: 'xs' }));
      nameRow.appendChild(badge);
    }

    infoWrapper.appendChild(nameRow);

    // File size and progress/error
    const detailRow = document.createElement('div');
    detailRow.className = 'flex items-center gap-2 mt-1';

    const fileSize = document.createElement('span');
    fileSize.className = 'text-xs text-muted-foreground';
    fileSize.textContent = formatFileSize(file.size);
    detailRow.appendChild(fileSize);

    if (file.status === 'uploading') {
      const progressText = document.createElement('span');
      progressText.className = 'text-xs text-primary';
      progressText.textContent = `${file.progress}%`;
      detailRow.appendChild(progressText);
    } else if (file.status === 'error' && file.error) {
      const errorText = document.createElement('span');
      errorText.className = 'text-xs text-destructive';
      errorText.textContent = file.error;
      detailRow.appendChild(errorText);
    }

    infoWrapper.appendChild(detailRow);

    // Progress bar for uploading files
    if (file.status === 'uploading') {
      const progressBarWrapper = document.createElement('div');
      progressBarWrapper.className = 'mt-2 h-1 w-full rounded-full bg-neutral-800 overflow-hidden';

      const progressBar = document.createElement('div');
      progressBar.className = 'h-full rounded-full bg-primary transition-all duration-300';
      progressBar.style.width = `${file.progress}%`;

      progressBarWrapper.appendChild(progressBar);
      infoWrapper.appendChild(progressBarWrapper);
    }

    item.appendChild(infoWrapper);

    // Remove button
    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-neutral-800 transition-colors shrink-0';
    removeBtn.title = 'Remove file';
    removeBtn.disabled = this.isDisabled || file.status === 'uploading';
    removeBtn.appendChild(renderIcon('x', { size: 'sm' }));

    removeBtn.addEventListener('click', () => {
      this.removeFile(file.id);
    });

    item.appendChild(removeBtn);

    return item;
  }

  /**
   * Remove a file
   */
  async removeFile(fileId: string): Promise<void> {
    const file = this.files.find((f) => f.id === fileId);
    if (!file) return;

    // If file was uploaded to server, delete it
    if (file.attachmentId && this.options.requestId) {
      try {
        await apiClient.delete(`/attachments/${file.attachmentId}`);
      } catch (error) {
        console.error('Failed to delete attachment:', error);
        // Continue with local removal even if server delete fails
      }
    }

    // Remove from local list
    this.files = this.files.filter((f) => f.id !== fileId);

    this.renderFileList();
    this.notifyChange();
    this.options.onRemove?.(file);
  }

  /**
   * Show error message
   */
  private showError(message: string): void {
    if (this.helperTextElement) {
      this.helperTextElement.textContent = message;
      this.helperTextElement.className = 'text-xs mt-2 text-destructive';
    } else {
      this.helperTextElement = document.createElement('p');
      this.helperTextElement.className = 'text-xs mt-2 text-destructive';
      this.helperTextElement.textContent = message;
      this.element?.appendChild(this.helperTextElement);
    }

    // Clear error after 5 seconds
    setTimeout(() => {
      if (this.helperTextElement && this.helperTextElement.textContent === message) {
        this.helperTextElement.textContent = this.options.helperText || '';
        this.helperTextElement.className = 'text-xs mt-2 text-muted-foreground';
      }
    }, 5000);
  }

  /**
   * Notify parent of file changes
   */
  private notifyChange(): void {
    this.options.onChange?.(this.files);
  }

  /**
   * Get the component element
   */
  getElement(): HTMLElement {
    return this.element || document.createElement('div');
  }

  /**
   * Get all files
   */
  getFiles(): UploadedFile[] {
    return [...this.files];
  }

  /**
   * Get completed files only
   */
  getCompletedFiles(): UploadedFile[] {
    return this.files.filter((f) => f.status === 'completed');
  }

  /**
   * Get attachment IDs for completed uploads
   */
  getAttachmentIds(): string[] {
    return this.files
      .filter((f) => f.status === 'completed' && f.attachmentId)
      .map((f) => f.attachmentId!);
  }

  /**
   * Check if any files are uploading
   */
  isUploading(): boolean {
    return this.files.some((f) => f.status === 'uploading');
  }

  /**
   * Check if there are any errors
   */
  hasErrors(): boolean {
    return this.files.some((f) => f.status === 'error');
  }

  /**
   * Set disabled state
   */
  setDisabled(disabled: boolean): this {
    this.isDisabled = disabled;
    this.updateDropzoneState(disabled ? 'disabled' : 'default');

    if (this.fileInput) {
      this.fileInput.disabled = disabled;
    }

    this.renderFileList();
    return this;
  }

  /**
   * Set validation state
   */
  setValidationState(state: ValidationState, message?: string): this {
    this.options.validationState = state;

    if (state === 'error') {
      this.updateDropzoneState('error');
      if (message) {
        this.showError(message);
      }
    } else {
      this.updateDropzoneState(this.isDisabled ? 'disabled' : 'default');
    }

    return this;
  }

  /**
   * Set request ID for uploads
   */
  setRequestId(requestId: string): this {
    this.options.requestId = requestId;
    return this;
  }

  /**
   * Clear all files
   */
  clear(): this {
    this.files = [];
    this.renderFileList();
    this.notifyChange();
    return this;
  }

  /**
   * Add event listener
   */
  on<K extends keyof HTMLElementEventMap>(
    type: K,
    listener: (event: HTMLElementEventMap[K]) => void
  ): this {
    this.element?.addEventListener(type, listener);
    return this;
  }

  /**
   * Remove event listener
   */
  off<K extends keyof HTMLElementEventMap>(
    type: K,
    listener: (event: HTMLElementEventMap[K]) => void
  ): this {
    this.element?.removeEventListener(type, listener);
    return this;
  }

  /**
   * Destroy the component
   */
  destroy(): void {
    if (this.element && this.container.contains(this.element)) {
      this.container.removeChild(this.element);
    }
    this.element = null;
    this.dropzone = null;
    this.fileInput = null;
    this.fileListContainer = null;
    this.helperTextElement = null;
    this.files = [];
  }
}

/**
 * Factory function to create a file upload component
 */
export function createFileUpload(options: FileUploadOptions = {}): FileUpload {
  return new FileUpload(options);
}
